import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LANGUAGES, t } from "@/i18n";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Globe, LogOut } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

/** `showNav` renders the Portfolio · Network · Come funziona · Contatti nav
 *  used on the public landing. Other pages hide it for a cleaner look. */
export default function Header({ showNav = false }) {
  const { user, lang, setLang, logout } = useApp();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const NAV_LINKS = [
    { key: "portfolio", label: t("nav_portfolio", lang), to: user ? "/dashboard" : "/login" },
    { key: "network",   label: t("nav_network",   lang), to: "/network" },
    { key: "how",       label: t("nav_how",       lang), to: "/come-funziona" },
    { key: "contacts",  label: t("nav_contacts",  lang), to: "/contatti" },
  ];

  return (
    <header className="w-full border-b border-[#E6DFCF] bg-[#F5F0E6]/95 backdrop-blur">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-10 lg:px-16">
        {/* Brand (text-based per reference) */}
        <Link to="/" className="flex flex-col leading-tight" data-testid="brand-logo">
          <span
            className="text-lg sm:text-xl tracking-[0.15em] text-[#1A1610]"
            style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 500 }}
          >
            AARON PROPERTIES
          </span>
          <span className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mt-0.5">
            {t("brand_subtitle", lang)}
          </span>
        </Link>

        <nav className="flex items-center gap-3 sm:gap-6">
          {showNav && (
            <ul className="hidden md:flex items-center gap-6 lg:gap-8">
              {NAV_LINKS.map((l) => (
                <li key={l.key}>
                  <Link
                    to={l.to}
                    data-testid={`nav-${l.key}`}
                    className="text-xs tracking-[0.25em] uppercase text-[#4A3F30] hover:text-[#B8965A] transition-colors duration-200"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="lang-switch-button" className="text-[#4A3F30] hover:text-[#1A1610] hover:bg-[#EFE7D5]">
                <Globe className="h-4 w-4 me-1" />
                <span className="text-xs tracking-widest uppercase">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-[#E6DFCF] text-[#1A1610]">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="cursor-pointer hover:bg-[#EFE7D5]"
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-button"
                  className="flex items-center gap-2 px-2 py-1 rounded-none border border-[#B8965A]/40 hover:bg-[#EFE7D5] transition-colors duration-200"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-[#B8965A] flex items-center justify-center">
                      <span className="text-white text-xs font-medium">{(user.name || "?").slice(0,1).toUpperCase()}</span>
                    </div>
                  )}
                  <span className="hidden sm:inline text-xs text-[#2E2A22]" data-testid="user-name">{user.name}</span>
                  <span className="hidden sm:inline text-[10px] tracking-widest uppercase text-[#B8965A]">{user.role}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-[#E6DFCF] text-[#1A1610] min-w-[200px]">
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-[#EFE7D5]">
                  <Link to="/" data-testid="menu-home" className="w-full">{t("home_nav", lang)}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-[#EFE7D5]">
                  <Link to="/dashboard" data-testid="menu-dashboard" className="w-full">{t("dashboard", lang)}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={doLogout}
                  data-testid="menu-logout"
                  className="cursor-pointer text-red-700 hover:text-red-800 hover:bg-red-50"
                >
                  <LogOut className="h-3.5 w-3.5 me-2" /> {t("logout", lang)}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <button
              data-testid="signin-header-button"
              onClick={() => navigate("/login")}
              className="border border-[#B8965A]/70 text-[#B8965A] hover:bg-[#B8965A] hover:text-white px-6 h-11 text-xs tracking-[0.3em] uppercase transition-colors duration-200"
            >
              Login
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
